<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-10" @close="$emit('close')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900">
                  <TableCellsIcon class="h-6 w-6 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Create New Worksheet
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      Define the structure for your new worksheet. This will create a new table in Snowflake.
                    </p>
                  </div>
                </div>
              </div>

              <div class="mt-5 space-y-4">
                 <div>
                    <label for="name" class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">Worksheet Name</label>
                    <div class="mt-2">
                      <input
                        type="text"
                        name="name"
                        id="name"
                        v-model="form.name"
                        class="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        placeholder="e.g., Q4 Sales Goals"
                      />
                    </div>
                 </div>

                 <div>
                    <div class="flex justify-between items-center mb-2">
                         <label class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">Columns</label>
                         <button @click="addColumn" type="button" class="text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-500">+ Add Column</button>
                    </div>
                    <div class="space-y-2 max-h-48 overflow-y-auto">
                        <div v-for="(col, index) in form.columns" :key="index" class="flex gap-2">
                             <input type="text" v-model="col.title" placeholder="Column Name" class="block w-1/2 rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 text-sm" />
                             <select v-model="col.type" class="block w-1/3 rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 text-sm">
                                 <option value="text">Text</option>
                                 <option value="numeric">Number</option>
                                 <option value="date">Date</option>
                                 <option value="checkbox">Checkbox</option>
                             </select>
                             <button @click="removeColumn(index)" type="button" class="text-red-500 hover:text-red-700 px-1">
                                 &times;
                             </button>
                        </div>
                    </div>
                 </div>
              </div>

              <div class="mt-6 sm:mt-8 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                  @click="create"
                  :disabled="!isValid"
                >
                  Create
                </button>
                <button
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:col-start-1 sm:mt-0"
                  @click="$emit('close')"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { TableCellsIcon } from '@heroicons/vue/24/outline'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits(['close', 'create'])

const form = reactive({
    name: '',
    columns: [
        { title: 'ID', type: 'text' },
        { title: 'Name', type: 'text' }
    ]
})

const isValid = computed(() => form.name.length > 0 && form.columns.length > 0);

function addColumn() {
    form.columns.push({ title: '', type: 'text' });
}

function removeColumn(index: number) {
    form.columns.splice(index, 1);
}

function create() {
    emit('create', { ...form });
    // Reset form
    form.name = '';
    form.columns = [{ title: 'ID', type: 'text' }, { title: 'Name', type: 'text' }];
}
</script>
